import React from 'react';
import styled, { css } from 'styled-components/macro';

import modularScale from 'styles/helpers/modular-scale';
import formatNumber from 'helpers/formatNumber';

const Pager = styled('div')<{ hasMoreInfo: boolean }>`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: space-between;
  margin: ${({ theme }): string => theme.spacing.medium} 0;

  @media screen and (max-width: 955px) {
    flex-flow: ${({ hasMoreInfo }): string =>
      hasMoreInfo ? 'column wrap' : 'row wrap'};
    align-items: flex-start;
  }
`;

const ShowMore = styled('button')<any>`
  font-weight: 400;
  color: ${({ theme }): string => theme.AccentGreen};
  text-align: left;
  flex: 1 0 auto;
  padding: 0;

  &:active {
    text-decoration: underline;
  }

  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }

  ${({ isWideButton }): any =>
    isWideButton &&
    css`
      border: 1px solid #0bd6a7;
      text-align: center;
      height: 50px;
      cursor: pointer;
      width: 100%;

      &:active {
        text-decoration: none;
        background: #daf9f2;
      }

      @media (hover: hover) {
        &:hover {
          text-decoration: none;
          background: #daf9f2;
        }
      }
    `}

  @media screen and (max-width: 60em) {
    ${({ hasMoreInfo }): any =>
      hasMoreInfo &&
      css`
        margin-bottom: 1.5em;
      `}
  }
`;

const PageIndication = styled('span')<any>`
  flex: 0 0 auto;
  margin-left: auto;
  white-space: nowrap;
  font-size: ${modularScale(-1)};
  color: ${({ theme }): string => theme.CasperGrey};
  margin-top: ${({ withTopMargin }): any => withTopMargin && '12px'};
  &.additional {
    &:after {
      content: '';
      display: inline-block;
      margin-left: calc(1em - 2px);
      border-right: 1px solid ${({ theme }): string => theme.CasperGrey};
      height: 10px;
    }

    @media screen and (max-width: 60em) {
      order: 2;
      margin-left: initial;

      &:after {
        display: none;
      }
    }
  }

  &:nth-of-type(2) {
    padding-left: 1em;
    margin-left: ${({ aligned }): any => aligned && 'initial'};

    @media screen and (max-width: 60em) {
      ${({ hasMoreInfo }): any =>
        hasMoreInfo &&
        css`
          padding-left: 0;
          margin-left: initial;
        `}
    }
  }
`;

interface IPaginationProps {
  current: number;
  customShowMoreText?: string;
  hasMoreResults: boolean;
  offset: number;
  onClick: Function;
  total?: number;
  type?: string;
  additionalInfo?: string;
  isWideButton?: boolean;
  showOnlyTotal?: boolean;
  isWideButtonWithTotal?: boolean;
}

const Pagination: React.FC<IPaginationProps> = ({
  current,
  customShowMoreText,
  hasMoreResults,
  offset,
  onClick,
  total,
  type,
  additionalInfo,
  isWideButtonWithTotal,
  // TODO remove 2 parameters below + remove isWideButtonWithTotal parameter when all tables will have new pagination design.
  isWideButton,
  showOnlyTotal,
}) => {
  let showMoreLabel = `Show ${offset} more`;
  if (total) {
    // Calculate length of remaining items and use this in label when it's lower than the offset
    const numberOfRemainingItems = total - current;
    const showMoreAmount =
      numberOfRemainingItems < offset ? numberOfRemainingItems : offset;
    // When the offset is set higher than total, change copy to "Show all"
    showMoreLabel =
      offset >= total ? 'Show all' : `Show ${showMoreAmount} more`;
  }

  return (
    <Pager hasMoreInfo={!!additionalInfo}>
      {hasMoreResults && !showOnlyTotal && (
        <ShowMore
          onClick={onClick}
          hasMoreInfo={additionalInfo}
          isWideButton={isWideButton || isWideButtonWithTotal}
        >
          {customShowMoreText ? customShowMoreText : showMoreLabel}
        </ShowMore>
      )}
      {additionalInfo && !showOnlyTotal && (
        <PageIndication
          className="additional"
          withTopMargin={isWideButtonWithTotal}
        >
          {additionalInfo}
        </PageIndication>
      )}
      {((total && !isWideButton) ||
        showOnlyTotal ||
        (total && isWideButtonWithTotal)) && (
        <PageIndication
          aligned={!hasMoreResults}
          hasMoreInfo={!!additionalInfo}
          withTopMargin={isWideButtonWithTotal}
        >
          Showing {Math.min(current, total || 0)} of{' '}
          {formatNumber(total || null)} {type}
        </PageIndication>
      )}
    </Pager>
  );
};

export default Pagination;
